/**
 * Form Text Input.
 * @format
 */
/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React from 'react';
import Translate from '@components/translation/translate-text-wrapper';
import { useSafeTranslate } from '@hooks/use-safe-translate';
import { FieldProperties, InputProperties } from '../form-modals';

/**
 * Form Text Input.
 *
 * @param {object} fieldProperties
 */
const TextInput = (props: InputProperties): JSX.Element => {
  const { fieldProperties, register, errors, fieldId, repeatingArrayKey } =
    props;
  const {
    type,
    id,
    fieldTitle,
    fieldName,
    required,
    placeholderText,
    errorMessage,
    textRows,
  }: FieldProperties = fieldProperties;
  const textInputId = fieldId || id;
  const fieldClasses = errors[fieldName] ? 'error' : '';
  const requiredClass = required ? 'required' : '';
  const rows = textRows || '5';

  /**
   * Get input ref for field validation.
   */
  const getInputRef = (fieldType: string) => {
    const ref = required ? { required: true } : {};
    const emailRef = {
      required: true,
      pattern:
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    };
    const urlRef = {
      required: true,
      pattern:
        /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/,
    };
    const textAreaRef = {
      required: true,
      validate: (value) => value.replace(/\s+/g, ' ').trim().length > 2,
    };

    let inputRef = ref;
    switch (fieldType) {
      case 'email':
        inputRef = emailRef;
        break;
      case 'url':
        inputRef = urlRef;
        break;
      case 'textarea':
        inputRef = textAreaRef;
        break;
    }
    return inputRef;
  };

  const textInputRef = getInputRef(type);

  /**
   * Label.
   */
  const label = fieldTitle ? (
    <label htmlFor={textInputId} className={requiredClass}>
      <Translate>{fieldTitle}</Translate>
    </label>
  ) : null;

  const placeHolder = useSafeTranslate(placeholderText, true);
  const safePlaceHolder = placeHolder || undefined;
  /**
   * Field.
   */
  const field =
    type === 'textarea' ? (
      <textarea
        className={fieldClasses}
        id={textInputId}
        name={fieldName}
        rows={parseInt(rows, 10)}
        placeholder={safePlaceHolder}
        {...register(fieldName, textInputRef)}
      />
    ) : (
      <input
        className={fieldClasses}
        id={textInputId}
        name={fieldName}
        type={type}
        placeholder={safePlaceHolder}
        {...register(fieldName, textInputRef)}
      />
    );

  /**
   * Error Message.
   *
   * @returns
   */
  const errorPrompt = (): JSX.Element | null => {
    let fieldError = null;
    const errorPrompt = (
      <p className="error-message">
        <Translate>{errorMessage}</Translate>
      </p>
    );

    if (repeatingArrayKey && errors[repeatingArrayKey]) {
      errors[repeatingArrayKey].forEach((field) => {
        const fieldLookupName = fieldId?.split('-')[0];
        if (fieldLookupName && field[fieldLookupName]) {
          if (field[fieldLookupName]?.ref?.id === fieldId) {
            fieldError = errorPrompt;
          }
        }
      });
    } else if (errors[fieldName]) {
      fieldError = errorPrompt;
    }
    return fieldError;
  };

  /**
   * Render.
   */
  return (
    <>
      {label}
      {field}
      {errorPrompt()}
    </>
  );
};
export default TextInput;
